// src/components/music/views/GroupedView.jsx

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Disc3, ChevronDown, ChevronUp, ListMusic } from 'lucide-react';
import { TableRow } from '../parts/TableRow';

export const GroupedView = ({
  groupedByAlbum,
  currentTrack,
  isPlaying,
  playQueue,
  onPlayPause,
  onPurchase,
  onPlayAlbum,
  processingPayment,
  currentUser,
  isPurchased,
  onDownload,
  onAddToPlaylist
}) => {
  const [expandedAlbums, setExpandedAlbums] = useState(
    Object.keys(groupedByAlbum).reduce((acc, album) => {
      acc[album] = true;
      return acc;
    }, {})
  );

  const toggleAlbum = (albumName) => {
    setExpandedAlbums(prev => ({
      ...prev,
      [albumName]: !prev[albumName]
    }));
  };

  return (
    <div className="space-y-6 mb-12 font-sans antialiased">
      {Object.entries(groupedByAlbum)
        .sort(([a], [b]) => {
          if (a === 'No Album') return 1;
          if (b === 'No Album') return -1;
          return a.localeCompare(b);
        })
        .map(([albumName, tracks]) => (
          <motion.div
            key={albumName}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#121318]/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden transition-all duration-300"
          >
            {/* Luxury Dark Album Header Banner (No Indigo Gradient) */}
            <div className="flex justify-between items-center p-3.5 sm:p-4 md:p-5 bg-[#181a22] border-b border-white/10 text-white">
              <button
                onClick={() => toggleAlbum(albumName)}
                className="flex items-center justify-between flex-1 hover:opacity-80 transition group pr-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 rounded-xl bg-[#bd9f67]/10 border border-[#bd9f67]/20 text-[#bd9f67] flex-shrink-0">
                    <Disc3 size={20} className="sm:w-5 sm:h-5" />
                  </div>
                  <div className="text-left min-w-0">
                    <h2 className="text-base sm:text-lg font-bold tracking-tight text-white/95 truncate">
                      {albumName}
                    </h2>
                    <span className="inline-block mt-0.5 text-[11px] font-semibold font-mono text-[#bd9f67] bg-[#bd9f67]/15 border border-[#bd9f67]/25 px-2.5 py-0.5 rounded-full">
                      {tracks.length} track{tracks.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-2 text-white/60 group-hover:text-white transition">
                  {expandedAlbums[albumName] ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </div>
              </button>

              <button
                onClick={() => onPlayAlbum(albumName)}
                className="ml-2 sm:ml-3 p-2.5 rounded-xl bg-[#bd9f67] text-gray-950 hover:bg-[#d0b07a] transition flex-shrink-0 shadow-md font-semibold"
                title={`Play all tracks in ${albumName}`}
              >
                <ListMusic size={18} fill="currentColor" />
              </button>
            </div>

            {/* Expanded Table Content */}
            <AnimatePresence>
              {expandedAlbums[albumName] && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-x-auto"
                >
                  <table className="w-full divide-y divide-white/5">
                    <thead className="bg-black/40">
                      <tr>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-white/50 uppercase tracking-wider">#</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-white/50 uppercase tracking-wider">Title</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-white/50 uppercase tracking-wider hidden sm:table-cell">Artist</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-white/50 uppercase tracking-wider hidden md:table-cell">Type</th>
                        <th className="px-4 py-3 text-right text-[11px] font-bold text-white/50 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-transparent divide-y divide-white/5">
                      {tracks.map((track, index) => (
                        <TableRow
                          key={track.id}
                          track={track}
                          index={index}
                          isCurrent={currentTrack?.id === track.id}
                          isPlaying={isPlaying}
                          playQueue={playQueue}
                          onPlayPause={onPlayPause}
                          onPurchase={onPurchase}
                          processingPayment={processingPayment}
                          currentUser={currentUser}
                          isPurchased={isPurchased}
                          onDownload={onDownload}
                          onAddToPlaylist={onAddToPlaylist}
                        />
                      ))}
                    </tbody>
                  </table>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
    </div>
  );
};
